import React, { useEffect, useMemo, useState } from "react";

import { ModalBase, MultipleSelectList, Spinner, Text } from "@performyard/py-components";

import { type FormOption, createRenderFormOption } from "./FormOption";
import { ModalActions } from "./ModalActions";

import { useAllReviewForms } from "../../../api/hooks/defs/forms";

export type { FormOption };

const EMPTY_ARRAY: string[] = [];

export type ModalSelectFormsProps = {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Called when the modal closes */
	onClose: () => void;
	/** Called with selected form base IDs on confirm */
	onConfirm: (selectedFormBaseIds: string[]) => void;
	/** Modal title @default "Select Forms" */
	title?: string;
	/** Form base IDs to exclude from selection */
	exclusionList?: string[];
	/** Initially selected form base IDs */
	initialSelection?: string[];
	/** Loading state for confirm button */
	isConfirming?: boolean;
};

const ModalSelectForms = ({
	isOpen,
	onClose,
	onConfirm,
	title = "Select Forms",
	exclusionList = EMPTY_ARRAY,
	initialSelection = EMPTY_ARRAY,
	isConfirming = false,
}: ModalSelectFormsProps) => {
	const [selectedFormIds, setSelectedFormIds] = useState<string[]>(initialSelection);
	const { data: formsData, isLoading } = useAllReviewForms({ enabled: isOpen });

	const { formOptions, formsById } = useMemo(() => {
		if (!formsData?.data) return { formOptions: [], formsById: new Map<string, FormOption>() };

		const exclusionSet = new Set(exclusionList);
		const formsById = new Map<string, FormOption>();

		const formOptions = formsData.data
			.filter((form) => !exclusionSet.has(form.baseId))
			.map((form) => {
				const formData: FormOption = {
					baseId: form.baseId,
					name: form.name,
				};
				formsById.set(form.baseId, formData);

				return {
					label: form.name,
					value: form.baseId,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label));

		return { formOptions, formsById };
	}, [formsData, exclusionList]);

	const renderFormOption = useMemo(() => createRenderFormOption(formsById), [formsById]);

	useEffect(() => {
		if (isOpen) {
			setSelectedFormIds(initialSelection);
		}
	}, [isOpen, initialSelection]);

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[432px] max-w-[90vw] !overflow-hidden">
			{/* Header */}
			<div className="px-6 pb-4">
				<div className="text-center">
					<Text size="sub-heading" weight="semi-bold">
						{title}
					</Text>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 px-6">
				<div className="relative min-h-[368px]">
					<div className={isLoading || isConfirming ? "opacity-50" : ""}>
						<MultipleSelectList<string>
							options={isLoading ? [] : formOptions}
							value={isLoading ? [] : selectedFormIds}
							onChange={setSelectedFormIds}
							searchPlaceholder="Search forms..."
							renderOption={renderFormOption}
							virtualizationConfig={{
								itemHeight: 32,
								maxHeight: 320,
							}}
						/>
					</div>
					{(isLoading || isConfirming) && (
						<div className="absolute inset-0 top-14 flex items-center justify-center">
							<Spinner size={32} />
						</div>
					)}
				</div>
			</div>

			{/* Footer - Actions */}
			<div className="sticky bottom-0 bg-neutral-0 px-6 pt-4 pb-6">
				<ModalActions
					selectedCount={selectedFormIds.length}
					isConfirming={isConfirming}
					onCancel={onClose}
					onConfirm={() => onConfirm(selectedFormIds)}
				/>
			</div>
		</ModalBase>
	);
};

export default ModalSelectForms;
